/** Limite acima do qual a árvore inicia recolhida (só raízes visíveis). */
export const LARGE_TREE_THRESHOLD = 250;

export function normalizeSearchText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function buildSlotSearchBlob(slot) {
  const botoes = Array.isArray(slot.botoes)
    ? slot.botoes.join(" ")
    : String(slot.botoes || "")
        .split(",")
        .join(" ");
  return normalizeSearchText(
    [slot.identificador, slot.nome, slot.resposta, botoes, slot.condicoes, slot.id]
      .filter((v) => v != null && String(v).trim() !== "")
      .join(" ")
  );
}

/** Índice id → texto de busca (calculado uma vez por carga de slots). */
export function buildSlotSearchIndex(slots) {
  const index = new Map();
  for (const slot of slots || []) {
    index.set(String(slot.id), buildSlotSearchBlob(slot));
  }
  return index;
}

export function buildChildrenByParent(slots) {
  const byParent = new Map();
  for (const s of slots || []) {
    const pid = s.id_pai != null ? String(s.id_pai) : "0";
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(s);
  }
  return byParent;
}

export function computeSearchState(slots, searchIndex, rawQuery) {
  const query = normalizeSearchText(rawQuery);
  if (!query) {
    return { visibleIds: null, expandIds: null, matchingIds: new Set(), query: "" };
  }

  const slotById = new Map((slots || []).map((s) => [String(s.id), s]));
  const matchingIds = new Set();
  const visibleIds = new Set();
  const expandIds = new Set();

  for (const slot of slots || []) {
    const id = String(slot.id);
    const blob = searchIndex.get(id) || "";
    if (blob.includes(query)) matchingIds.add(id);
  }

  for (const id of matchingIds) {
    let current = slotById.get(id);
    while (current) {
      const cid = String(current.id);
      visibleIds.add(cid);
      expandIds.add(cid);
      const parentId = current.id_pai;
      current =
        parentId != null && Number(parentId) !== 0 ? slotById.get(String(parentId)) : null;
    }
  }

  return { visibleIds, expandIds, matchingIds, query };
}

export function highlightSnippet(text, query, maxLen = 72) {
  const raw = String(text || "").trim();
  if (!raw || !query) return null;
  const normRaw = normalizeSearchText(raw);
  const idx = normRaw.indexOf(query);
  if (idx < 0) return null;
  const start = Math.max(0, idx - 12);
  const end = Math.min(raw.length, idx + query.length + 36);
  let snippet = raw.slice(start, end);
  if (start > 0) snippet = `…${snippet}`;
  if (end < raw.length) snippet = `${snippet}…`;
  return snippet.length > maxLen ? `${snippet.slice(0, maxLen - 1)}…` : snippet;
}

/**
 * Lista plana de linhas visíveis para virtualização (sem recursão React).
 */
export function flattenVisibleTreeRows({
  roots,
  childrenByParent,
  openNodes,
  visibleIds,
  isLargeTree,
  forceExpand,
}) {
  const rows = [];

  const isNodeOpen = (id) => {
    if (forceExpand) return true;
    if (isLargeTree) return openNodes[id] === true;
    return openNodes[id] !== false;
  };

  const walk = (slot, level) => {
    const id = String(slot.id);
    if (visibleIds && !visibleIds.has(id)) return;

    const children = childrenByParent.get(id) || [];
    const visibleChildren = visibleIds
      ? children.filter((c) => visibleIds.has(String(c.id)))
      : children;
    const hasChildren = visibleChildren.length > 0;
    const isOpen = hasChildren && isNodeOpen(id);

    rows.push({
      id,
      slot,
      level,
      hasChildren,
      isOpen,
    });

    if (isOpen) {
      for (const child of visibleChildren) {
        walk(child, level + 1);
      }
    }
  };

  const visibleRoots = visibleIds
    ? roots.filter((r) => visibleIds.has(String(r.id)))
    : roots;

  for (const root of visibleRoots) {
    walk(root, 0);
  }

  return rows;
}

/** Estado inicial: árvores grandes começam com ramos recolhidos. */
export function buildInitialOpenNodes(slots, isLargeTree) {
  if (!isLargeTree) return {};
  const childrenByParent = buildChildrenByParent(slots);
  const open = {};
  for (const slot of slots || []) {
    const id = String(slot.id);
    if ((childrenByParent.get(id) || []).length > 0) {
      open[id] = false;
    }
  }
  return open;
}

export function buildExpandAllOpenNodes(slots) {
  const childrenByParent = buildChildrenByParent(slots);
  const open = {};
  for (const [id, children] of childrenByParent) {
    if (id !== "0" && children.length > 0) open[id] = true;
  }
  for (const slot of slots || []) {
    const id = String(slot.id);
    if ((childrenByParent.get(id) || []).length > 0) open[id] = true;
  }
  return open;
}

export function buildCollapseAllOpenNodes(slots) {
  return buildInitialOpenNodes(slots, true);
}
